import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';
import { Theme } from '../../modules/private/configuracoes/identidade-visual/models/theme.model';
import { SystemTitleService } from '../../modules/private/shared/services/system-title.service';
import { TenantTitleStrategy } from './tenant-title-strategy';

const FAVICON_SELECTOR = 'link[rel="icon"]';
const BRAND_FONT_FAMILY_VAR = '--Brand-FontFamily';
const SURFACE_ON_SURFACE_VAR = '--Surface-OnSurface';

/**
 * Papéis obrigatórios do backend mapeados para custom properties CSS.
 * Os fallbacks de build vivem no `:root` de `src/styles.sass`, que também faz a ponte
 * estática para os tokens nativos do Material (`--mat-sys-primary` etc.) via
 * `var()`/`color-mix()` — decisão 6/§3.5 do plano, resolvida em 2026-07-11. Este serviço
 * segue NÃO setando `--mat-sys-*` inline: sobrescrever a camada de marca abaixo basta
 * para a cascata propagar o tema aos componentes Material.
 */
export const TENANT_TOKEN_MAP: Record<string, keyof Theme> = {
  '--Primary-PrimaryBlueDark': 'primary_color',
  '--Primary-OnPrimary': 'on_primary_color',
  '--Secondary-Secondary': 'secondary_color',
  '--Secondary-OnSecondary': 'on_secondary_color',
};

/** Papéis opcionais no backend — só aplicados quando o tema realmente os define. */
const OPTIONAL_TOKEN_MAP: Record<string, keyof Theme> = {
  '--Surface': 'surface_color',
  '--Feedback-Alert': 'error_color',
};

@Injectable({ providedIn: 'root' })
export class ThemeApplierService {
  private readonly document = inject(DOCUMENT);
  private readonly titleStrategy = inject(TenantTitleStrategy);
  private readonly systemTitleService = inject(SystemTitleService);

  private readonly activeLogoUrlSignal = signal<string | null>(null);
  private readonly activeEmblemUrlSignal = signal<string | null>(null);

  /** Consumido por `HeaderComponent` — fallback para `env.logo` quando `null` (T06). */
  readonly activeLogoUrl = this.activeLogoUrlSignal.asReadonly();

  /** Consumido por `LoginComponent` — fallback para `env.logo2`/`haveTwoLogos` quando `null`. */
  readonly activeEmblemUrl = this.activeEmblemUrlSignal.asReadonly();

  apply(theme: Theme): void {
    const root = this.document.documentElement;

    this.setToken(root, TENANT_TOKEN_MAP, theme);
    this.setToken(root, OPTIONAL_TOKEN_MAP, theme);

    if (theme.surface_color) {
      root.style.setProperty(SURFACE_ON_SURFACE_VAR, pickReadableTextColor(theme.surface_color));
    }

    // Guardas de string não vazia: um tema malformado (ex.: envelope da API não
    // desembrulhado, regressão real de 2026-07-11) não pode virar a fonte literal
    // "undefined" nem um título vazio — melhor manter o valor de build.
    if (typeof theme.font_family === 'string' && theme.font_family) {
      root.style.setProperty(BRAND_FONT_FAMILY_VAR, theme.font_family);
    }

    if (typeof theme.main_title === 'string' && theme.main_title) {
      this.systemTitleService.set(theme.main_title);
      this.titleStrategy.setTenantTitle(theme.main_title);
    }

    this.applyFavicon(theme.favicon_url);

    this.activeLogoUrlSignal.set(theme.logo_url ?? null);
    this.activeEmblemUrlSignal.set(theme.emblem_url ?? null);
  }

  private setToken(root: HTMLElement, map: Record<string, keyof Theme>, theme: Theme): void {
    for (const [cssVar, field] of Object.entries(map)) {
      const value = theme[field];
      if (typeof value === 'string' && value) {
        root.style.setProperty(cssVar, value);
      }
    }
  }

  /**
   * Favicon dinâmico — divergência 6 do plano, confirmada em código no
   * integration-guide (4).md: `favicon_url` segue o mesmo padrão de `emblem_url`/`logo_url`.
   * Sem esse campo (tema ainda sem favicon definido), mantém o favicon estático do build
   * (`src/index.html`).
   */
  private applyFavicon(faviconUrl: string | undefined): void {
    if (!faviconUrl) {
      return;
    }
    const link = this.document.querySelector<HTMLLinkElement>(FAVICON_SELECTOR);
    if (link) {
      link.href = faviconUrl;
    }
  }
}

const HEX_PATTERN = /^#([0-9A-Fa-f]{6})$/;

function hexToRgb(hex: string): [number, number, number] | null {
  const match = HEX_PATTERN.exec(hex);
  if (!match) {
    return null;
  }
  const value = match[1];
  return [
    parseInt(value.substring(0, 2), 16),
    parseInt(value.substring(2, 4), 16),
    parseInt(value.substring(4, 6), 16),
  ];
}

function toLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(rgb: [number, number, number]): number {
  const [r, g, b] = rgb.map(toLinear);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastRatio(hexA: string, hexB: string): number {
  const rgbA = hexToRgb(hexA);
  const rgbB = hexToRgb(hexB);
  if (!rgbA || !rgbB) {
    return 1;
  }
  const luminanceA = relativeLuminance(rgbA);
  const luminanceB = relativeLuminance(rgbB);
  const lighter = Math.max(luminanceA, luminanceB);
  const darker = Math.min(luminanceA, luminanceB);
  return (lighter + 0.05) / (darker + 0.05);
}

/** Escolhe preto ou branco como cor de texto sobre `backgroundHex`, o que der mais contraste. */
function pickReadableTextColor(backgroundHex: string): '#000000' | '#FFFFFF' {
  return contrastRatio(backgroundHex, '#000000') >= contrastRatio(backgroundHex, '#FFFFFF')
    ? '#000000'
    : '#FFFFFF';
}
